import React, { useEffect, useMemo, useRef, useState } from 'react';
import { VendorBranding, VendorOfferShell } from './VendorOfferChrome';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const IOS_APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL ||
  'https://apps.apple.com/us/app/heal-music-breath/id6746941693';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SAVE_DELAY_MS = 400;

type Phase = 'loading' | 'welcome' | 'email' | 'questionnaire' | 'declined' | 'thanks' | 'linked' | 'error';

type AnswerValue = {
  choice?: string;
  first_name?: string;
  surname?: string;
  email?: string;
  confirm?: string;
  text?: string;
  detail?: string;
  prefer_not_to_say?: boolean;
};

type QuestionOption = { value: string; label: string };
type QuestionConfig = {
  options?: QuestionOption[];
  groups?: { label: string; options: QuestionOption[] }[];
  fields?: { key: 'first_name' | 'surname'; label: string; placeholder: string }[];
  placeholder?: string;
  confirm_placeholder?: string;
  label?: string;
  decline_label?: string;
  detail_label?: string;
  detail_placeholder?: string;
  detail_when?: string;
  section_label?: string;
  section_intro?: string;
  mismatch_label?: string;
};

type Question = {
  key: string;
  section: string;
  prompt: string;
  help_text: string | null;
  kind: string;
  required: boolean;
  position: number;
  numbered: boolean;
  config: QuestionConfig;
};

type CopyBlock = { eyebrow?: string; title?: string; paragraphs?: string[] };

type Definition = {
  slug: string;
  title: string;
  version: number;
  intro: CopyBlock;
  thank_you: CopyBlock;
  questions: Question[];
};

type Session = {
  slug: string;
  status: string;
  step: string;
  answers: Record<string, AnswerValue>;
};

type StoredSession = { claimId: string; resumeToken: string };

type ProgressRequest = {
  step: string;
  answers: Record<string, AnswerValue>;
  decline?: boolean;
};

type NhsTrialFlowProps = {
  token: string;
  branding: VendorBranding;
};

function storageKey(token: string) {
  return `heal.nhsQuestionnaire.${token}`;
}

function readStored(token: string): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(storageKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed.claimId || !parsed.resumeToken) return null;
    return { claimId: parsed.claimId, resumeToken: parsed.resumeToken };
  } catch {
    return null;
  }
}

function writeStored(token: string, claimId: string, resumeToken: string) {
  window.localStorage.setItem(storageKey(token), JSON.stringify({ claimId, resumeToken }));
}

function clearStored(token: string) {
  window.localStorage.removeItem(storageKey(token));
}

function errorDetail(data: unknown, fallback: string) {
  if (data && typeof data === 'object' && 'detail' in data && typeof (data as { detail: unknown }).detail === 'string') {
    return (data as { detail: string }).detail;
  }
  return fallback;
}

function isDefinition(value: unknown): value is Definition {
  return Boolean(value && typeof value === 'object' && Array.isArray((value as Definition).questions));
}

function isSession(value: unknown): value is Session {
  if (!value || typeof value !== 'object') return false;
  const session = value as Session;
  return typeof session.step === 'string' && typeof session.status === 'string' && !!session.answers;
}

async function fetchDefinition(slug: string): Promise<Definition | null> {
  const response = await fetch(`${API_URL}/vendor-offers/questionnaires/${encodeURIComponent(slug)}`);
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok || !isDefinition(data)) return null;
  return data;
}

async function fetchSession(stored: StoredSession): Promise<{ status: number; session: Session | null }> {
  const response = await fetch(`${API_URL}/vendor-offers/claims/${encodeURIComponent(stored.claimId)}/questionnaire`, {
    headers: { 'X-Questionnaire-Resume': stored.resumeToken },
  });
  const data: unknown = await response.json().catch(() => null);
  return { status: response.status, session: response.ok && isSession(data) ? data : null };
}

function FormattedBody({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="vendor-offer-subtitle">
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </p>
  );
}

function WebsiteLink() {
  return (
    <a className="vendor-offer-back" href="/">
      Back to Heal’s website
    </a>
  );
}

function isAnswerReady(question: Question, value: AnswerValue | undefined) {
  if (!question.required) return true;
  const current = value || {};
  if (question.kind === 'single_choice') return Boolean(current.choice);
  if (question.kind === 'name') return Boolean(current.first_name?.trim() && current.surname?.trim());
  if (question.kind === 'email_confirm') {
    const email = current.email?.trim() || '';
    const confirm = current.confirm?.trim() || '';
    return Boolean(email && confirm && email.toLowerCase() === confirm.toLowerCase() && EMAIL_RE.test(email));
  }
  if (question.kind === 'text') return Boolean(current.text?.trim());
  if (question.kind === 'measurement') return current.prefer_not_to_say === true || Boolean(current.text?.trim());
  if (question.kind === 'choice_with_detail') {
    if (!current.choice) return false;
    if (current.choice === question.config.detail_when) return Boolean(current.detail?.trim());
    return true;
  }
  return false;
}

function ChoiceButtons({
  options,
  selected,
  onSelect,
}: {
  options: QuestionOption[];
  selected: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="questionnaire-choices">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`questionnaire-choice${selected === option.value ? ' selected' : ''}`}
          aria-pressed={selected === option.value}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function QuestionFields({
  question,
  answer,
  numberedTotal,
  onChange,
}: {
  question: Question;
  answer: AnswerValue;
  numberedTotal: number;
  onChange: (recipe: (current: AnswerValue) => AnswerValue, when: 'debounce' | 'now') => void;
}) {
  const titleId = `question-${question.key}`;
  const help = question.help_text ? (
    <p className="questionnaire-help" id={`${titleId}-help`}>
      {question.kind === 'email_confirm' ? `*${question.help_text}` : question.help_text}
    </p>
  ) : null;
  const describedBy = question.help_text ? `${titleId}-help` : undefined;

  const choices = (
    <>
      {(question.config.groups || []).map((group) => (
        <div key={group.label}>
          {group.label ? <p className="questionnaire-group">{group.label}</p> : null}
          <ChoiceButtons
            options={group.options}
            selected={answer.choice || ''}
            onSelect={(choice) => onChange(() => ({ choice }), 'now')}
          />
        </div>
      ))}
      {question.config.options?.length ? (
        <ChoiceButtons
          options={question.config.options}
          selected={answer.choice || ''}
          onSelect={(choice) =>
            onChange(
              (current) => ({
                choice,
                detail: choice === question.config.detail_when ? current.detail || '' : '',
              }),
              'now'
            )
          }
        />
      ) : null}
    </>
  );

  const mismatch = question.kind === 'email_confirm' && emailsMismatch(answer);

  const heartNote =
    question.key === 'heart_condition' && question.config.section_intro ? (
      <p className="questionnaire-help">*{question.config.section_intro}</p>
    ) : null;

  return (
    <div className="questionnaire-question" role="group" aria-labelledby={titleId}>
      {question.key === 'consent' ? (
        <div className="questionnaire-header questionnaire-header-consent">
          <h1 className="questionnaire-title" id={titleId}>
            Consent
          </h1>
          <p className="questionnaire-copy">{question.prompt}</p>
        </div>
      ) : (
        <div className="questionnaire-header">
          {question.numbered ? (
            <p className="questionnaire-progress">
              Question · {question.position} of {numberedTotal}
            </p>
          ) : null}
          <h1 className="questionnaire-prompt" id={titleId}>
            {question.key === 'heart_condition' ? `${question.prompt}*` : question.prompt}
          </h1>
        </div>
      )}
      <div className="questionnaire-fields">
        {question.kind === 'name'
          ? (question.config.fields || []).map((field) => (
              <label key={field.key} className="questionnaire-field">
                <span className="questionnaire-label">{field.label}</span>
                <input
                  value={answer[field.key] || ''}
                  placeholder={field.placeholder}
                  autoComplete={field.key === 'first_name' ? 'given-name' : 'family-name'}
                  autoCapitalize="words"
                  maxLength={100}
                  onChange={(event) => {
                    const next = event.target.value;
                    onChange((current) => ({ ...current, [field.key]: next }), 'debounce');
                  }}
                />
              </label>
            ))
          : null}
        {question.kind === 'email_confirm' ? (
          <>
            <label className="questionnaire-field">
              <span className="questionnaire-label">Enter email</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Your answer"
                maxLength={254}
                value={answer.email || ''}
                onChange={(event) => {
                  const next = event.target.value;
                  onChange((current) => ({ ...current, email: next }), 'debounce');
                }}
              />
            </label>
            <label className="questionnaire-field">
              <span className="questionnaire-label-row">
                <span className="questionnaire-label">Re enter email</span>
                {mismatch ? (
                  <span className="questionnaire-error">{question.config.mismatch_label || 'Emails don’t match'}</span>
                ) : null}
              </span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                aria-invalid={mismatch}
                aria-describedby={describedBy}
                className={mismatch ? 'questionnaire-input-invalid' : undefined}
                placeholder="Your answer"
                maxLength={254}
                value={answer.confirm || ''}
                onChange={(event) => {
                  const next = event.target.value;
                  onChange((current) => ({ ...current, confirm: next }), 'debounce');
                }}
              />
            </label>
          </>
        ) : null}
        {question.kind === 'text' || question.kind === 'long_text' ? (
          <label className="questionnaire-field">
            {question.config.label ? <span className="questionnaire-label">{question.config.label}</span> : null}
            {question.kind === 'long_text' ? (
              <textarea
                value={answer.text || ''}
                placeholder={question.config.placeholder || '(Optional)'}
                maxLength={2000}
                rows={4}
                aria-describedby={describedBy}
                onChange={(event) => {
                  const next = event.target.value;
                  onChange(() => ({ text: next }), 'debounce');
                }}
              />
            ) : (
              <input
                value={answer.text || ''}
                placeholder={question.config.placeholder || 'Your answer'}
                maxLength={500}
                aria-describedby={describedBy}
                onChange={(event) => {
                  const next = event.target.value;
                  onChange(() => ({ text: next }), 'debounce');
                }}
              />
            )}
          </label>
        ) : null}
        {question.kind === 'measurement' ? (
          <>
            <input
              value={answer.prefer_not_to_say ? '' : answer.text || ''}
              placeholder={question.config.placeholder || 'Your answer'}
              maxLength={100}
              disabled={answer.prefer_not_to_say === true}
              aria-describedby={describedBy}
              onChange={(event) => {
                const next = event.target.value;
                onChange(() => ({ text: next, prefer_not_to_say: false }), 'debounce');
              }}
            />
            <ChoiceButtons
              options={[{ value: 'prefer_not_to_say', label: question.config.decline_label || 'Prefer not to say' }]}
              selected={answer.prefer_not_to_say ? 'prefer_not_to_say' : ''}
              onSelect={() =>
                onChange(
                  (current) => ({
                    text: current.prefer_not_to_say ? current.text || '' : '',
                    prefer_not_to_say: !current.prefer_not_to_say,
                  }),
                  'now'
                )
              }
            />
          </>
        ) : null}
        {question.kind === 'single_choice' || question.kind === 'choice_with_detail' ? choices : null}
        {heartNote}
        {question.kind === 'choice_with_detail' && answer.choice === question.config.detail_when ? (
          <label className="questionnaire-field">
            <span className="questionnaire-label">{question.config.detail_label}</span>
            <textarea
              value={answer.detail || ''}
              placeholder={question.config.detail_placeholder || 'Brief details'}
              maxLength={2000}
              rows={3}
              onChange={(event) => {
                const next = event.target.value;
                onChange((current) => ({ ...current, detail: next }), 'debounce');
              }}
            />
          </label>
        ) : null}
      </div>
      {help}
    </div>
  );
}

function emailsMismatch(answer: AnswerValue) {
  const email = answer.email?.trim() || '';
  const confirm = answer.confirm?.trim() || '';
  return Boolean(email && confirm && email.toLowerCase() !== confirm.toLowerCase());
}

function QuestionnaireStep({
  claimId,
  resumeToken,
  definition,
  initialSession,
  onSession,
  onDeclined,
  onThanks,
}: {
  claimId: string;
  resumeToken: string;
  definition: Definition;
  initialSession: Session;
  onSession: (session: Session) => void;
  onDeclined: () => void;
  onThanks: () => void;
}) {
  const questions = useMemo(
    () => [...definition.questions].sort((left, right) => left.position - right.position),
    [definition.questions]
  );
  const steps = useMemo(() => ['intro', ...questions.map((question) => question.key)], [questions]);
  const numberedTotal = questions.filter((question) => question.numbered).length;
  const startingStep = steps.includes(initialSession.step) ? initialSession.step : 'intro';
  const [step, setStep] = useState(startingStep);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>(initialSession.answers || {});
  const [saveError, setSaveError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const answersRef = useRef(answers);
  const stepRef = useRef(step);
  const timer = useRef<number | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const onSessionRef = useRef(onSession);
  onSessionRef.current = onSession;

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const question = questions.find((item) => item.key === step) || null;
  const ready = step === 'intro' || (question ? isAnswerReady(question, answers[question.key]) : false);

  function applyStep(next: string) {
    stepRef.current = next;
    setStep(next);
  }

  async function sendProgress(body: ProgressRequest): Promise<Session | null> {
    try {
      const response = await fetch(`${API_URL}/vendor-offers/claims/${encodeURIComponent(claimId)}/questionnaire`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Questionnaire-Resume': resumeToken,
        },
        body: JSON.stringify(body),
      });
      const data: unknown = await response.json().catch(() => null);
      if (!response.ok || !isSession(data)) {
        setSaveError(errorDetail(data, 'We couldn’t save that answer. Check your connection and try again.'));
        return null;
      }
      setSaveError('');
      onSessionRef.current(data);
      return data;
    } catch {
      setSaveError('We couldn’t save that answer. Check your connection and try again.');
      return null;
    }
  }

  function runSave(body: ProgressRequest) {
    const job = queue.current.then(() => sendProgress(body));
    queue.current = job.then(
      () => undefined,
      () => undefined
    );
    return job;
  }

  function updateAnswer(questionKey: string, recipe: (current: AnswerValue) => AnswerValue, when: 'debounce' | 'now') {
    const nextValue = recipe(answersRef.current[questionKey] || {});
    const nextAnswers = { ...answersRef.current, [questionKey]: nextValue };
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    const body = { step: stepRef.current, answers: { [questionKey]: nextValue } };
    if (timer.current) window.clearTimeout(timer.current);
    if (when === 'now') {
      void runSave(body);
      return;
    }
    timer.current = window.setTimeout(() => {
      void runSave(body);
    }, SAVE_DELAY_MS);
  }

  async function goBack() {
    const index = steps.indexOf(stepRef.current);
    if (index <= 0 || submitting) return;
    if (timer.current) window.clearTimeout(timer.current);
    const previous = steps[index - 1];
    const currentKey = steps[index];
    setSubmitting(true);
    try {
      const payload = currentKey === 'intro' ? {} : { [currentKey]: answersRef.current[currentKey] || {} };
      const saved = await runSave({ step: previous, answers: payload });
      if (saved) applyStep(saved.step);
    } finally {
      setSubmitting(false);
    }
  }

  async function goForward() {
    if (submitting || !ready) return;
    if (timer.current) window.clearTimeout(timer.current);
    setSubmitting(true);
    setSaveError('');
    try {
      if (question?.key === 'consent' && answersRef.current.consent?.choice === 'no') {
        const saved = await runSave({
          step: 'consent',
          answers: { consent: { choice: 'no' } },
          decline: true,
        });
        if (saved?.status === 'declined') onDeclined();
        return;
      }
      if (question?.key === 'anything_else') {
        const value = answersRef.current.anything_else || { text: '' };
        const saved = await runSave({ step: 'anything_else', answers: { anything_else: value } });
        if (!saved) return;
        const response = await fetch(
          `${API_URL}/vendor-offers/claims/${encodeURIComponent(claimId)}/questionnaire/complete`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Questionnaire-Resume': resumeToken,
            },
            body: JSON.stringify({ answers: { anything_else: value } }),
          }
        );
        const data: unknown = await response.json().catch(() => null);
        if (!response.ok || !isSession(data)) {
          setSaveError(errorDetail(data, 'We couldn’t submit your questionnaire. Check your connection and try again.'));
          return;
        }
        onSessionRef.current(data);
        if (data.status === 'completed') onThanks();
        return;
      }
      const next = steps[steps.indexOf(stepRef.current) + 1];
      if (!next) return;
      const payload = question ? { [question.key]: answersRef.current[question.key] || {} } : {};
      const saved = await runSave({ step: next, answers: payload });
      if (saved) applyStep(saved.step);
    } finally {
      setSubmitting(false);
    }
  }

  const isLast = question?.key === 'anything_else';
  const showBack = steps.indexOf(step) > 0;

  return (
    <VendorOfferShell branding={{ vendorName: null, logoUrl: null, headline: null, bodyText: null, successText: null, questionnaireSlug: null }} showBranding={false} layout="questionnaire">
      <form
        className="questionnaire-form"
        onSubmit={(event) => {
          event.preventDefault();
          void goForward();
        }}
      >
        <div className="questionnaire-body">
          {step === 'intro' ? (
            <div className="questionnaire-intro">
              {definition.intro.eyebrow ? <p className="questionnaire-eyebrow">{definition.intro.eyebrow}</p> : null}
              <h1 className="questionnaire-title">{definition.intro.title}</h1>
              {(definition.intro.paragraphs || []).map((paragraph) => (
                <p className="questionnaire-copy" key={paragraph}>
                  {paragraph}
                </p>
              ))}
            </div>
          ) : question ? (
            <QuestionFields
              question={question}
              answer={answers[question.key] || {}}
              numberedTotal={numberedTotal}
              onChange={(recipe, when) => updateAnswer(question.key, recipe, when)}
            />
          ) : null}
        </div>
        <div className="questionnaire-footer">
          {saveError ? <p className="questionnaire-error">{saveError}</p> : null}
          <button
            type="submit"
            className={`questionnaire-next${ready && !submitting ? ' ready' : ''}`}
            disabled={!ready || submitting}
          >
            {submitting ? 'Saving…' : isLast ? 'Submit' : 'Continue'}
          </button>
          {showBack ? (
            <button type="button" className="questionnaire-back" onClick={() => void goBack()} disabled={submitting}>
              Back
            </button>
          ) : null}
        </div>
      </form>
    </VendorOfferShell>
  );
}

const NhsTrialFlow: React.FC<NhsTrialFlowProps> = ({ token, branding }) => {
  const slug = branding.questionnaireSlug || '';
  const [phase, setPhase] = useState<Phase>(() => (readStored(token) ? 'loading' : 'welcome'));
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [flowError, setFlowError] = useState('');
  const [definition, setDefinition] = useState<Definition | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [claimId, setClaimId] = useState<string | null>(null);
  const [resumeToken, setResumeToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const stored = readStored(token);
    if (!stored || !slug) {
      setPhase('welcome');
      return;
    }
    setPhase('loading');

    const restore = async () => {
      try {
        const [definitionResult, sessionResult] = await Promise.all([
          fetchDefinition(slug),
          fetchSession(stored),
        ]);
        if (cancelled) return;
        if (sessionResult.status === 401) {
          clearStored(token);
          setPhase('welcome');
          return;
        }
        if (!definitionResult || !sessionResult.session) {
          setFlowError('We couldn’t load your questionnaire. Check your connection and try again.');
          setPhase('error');
          return;
        }
        setDefinition(definitionResult);
        setSession(sessionResult.session);
        setClaimId(stored.claimId);
        setResumeToken(stored.resumeToken);
        setPhase(sessionResult.session.status === 'completed' ? 'linked' : sessionResult.session.status === 'declined' ? 'declined' : 'questionnaire');
      } catch {
        if (!cancelled) {
          setFlowError('We couldn’t load your questionnaire. Check your connection and try again.');
          setPhase('error');
        }
      }
    };

    void restore();
    return () => {
      cancelled = true;
    };
  }, [token, slug]);

  const openSession = async (nextClaimId: string, nextResume: string, status: string) => {
    writeStored(token, nextClaimId, nextResume);
    if (status === 'completed') {
      setClaimId(nextClaimId);
      setResumeToken(nextResume);
      setPhase('linked');
      return;
    }
    const [definitionResult, sessionResult] = await Promise.all([
      fetchDefinition(slug),
      fetchSession({ claimId: nextClaimId, resumeToken: nextResume }),
    ]);
    if (!definitionResult || !sessionResult.session) {
      setFlowError('We couldn’t open your questionnaire. Check your connection and try again.');
      setPhase('error');
      return;
    }
    setDefinition(definitionResult);
    setSession(sessionResult.session);
    setClaimId(nextClaimId);
    setResumeToken(nextResume);
    setPhase(sessionResult.session.status === 'declined' ? 'declined' : 'questionnaire');
  };

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setEmailError('');
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/vendor-offers/claims`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email: email.trim() }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        setEmailError(errorDetail(data, 'Failed to claim this offer.'));
        return;
      }
      const summary = data?.questionnaire;
      if (!summary?.resume_token || !data?.claim_id) {
        setEmailError('This offer is missing its questionnaire. Ask for an updated link.');
        return;
      }
      await openSession(data.claim_id, summary.resume_token, summary.status);
    } catch {
      setEmailError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <VendorOfferShell branding={branding}>
        <div className="vendor-offer-main">
          <p className="vendor-offer-subtitle vendor-offer-loading">Loading your questionnaire…</p>
        </div>
      </VendorOfferShell>
    );
  }

  if (phase === 'error') {
    return (
      <VendorOfferShell branding={branding}>
        <div className="vendor-offer-main">
          <h1 className="vendor-offer-title">Couldn’t load your questionnaire</h1>
          <p className="vendor-offer-error-screen">{flowError}</p>
          <button type="button" className="vendor-offer-submit vendor-offer-submit-link" onClick={() => setPhase('welcome')}>
            Start again
          </button>
        </div>
        <WebsiteLink />
      </VendorOfferShell>
    );
  }

  if (phase === 'welcome') {
    return (
      <VendorOfferShell branding={branding}>
        <div className="vendor-offer-main">
          <h1 className="vendor-offer-title">Welcome to Heal</h1>
          <p className="vendor-offer-subtitle">
            Thank you for participating in Heal’s clinical trial with the NHS.
          </p>
          <button type="button" className="vendor-offer-submit vendor-offer-submit-link" onClick={() => setPhase('email')}>
            Let’s Get Started
          </button>
        </div>
        <WebsiteLink />
      </VendorOfferShell>
    );
  }

  if (phase === 'email') {
    const body =
      branding.bodyText ||
      'Enter the email you’ll use for Heal. Premium is included for 12 months from the first time you open the app.';
    return (
      <VendorOfferShell branding={branding}>
        <div className="vendor-offer-main">
          <h1 className="vendor-offer-title">Provide your details</h1>
          <FormattedBody text={body} />
          <form onSubmit={submitEmail} className="vendor-offer-form">
            <input
              type="email"
              name="email"
              placeholder="Enter Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={submitting}
              autoComplete="email"
              required
            />
            {emailError ? <p className="vendor-offer-error">{emailError}</p> : null}
            <button type="submit" className={`vendor-offer-submit${submitting ? ' disabled' : ''}`} disabled={submitting}>
              {submitting ? 'Saving…' : 'Continue'}
            </button>
          </form>
        </div>
        <WebsiteLink />
      </VendorOfferShell>
    );
  }

  if (phase === 'linked') {
    const successText =
      branding.successText ||
      'Download Heal’s iOS app and sign in with this email. You’ll have premium for 12 months from your first sign-in, with nothing to pay.';
    return (
      <VendorOfferShell branding={branding}>
        <div className="vendor-offer-main">
          <h1 className="vendor-offer-title">Offer Linked</h1>
          <p className="vendor-offer-subtitle">{successText}</p>
          <a className="vendor-offer-submit vendor-offer-submit-link" href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            Open in the App Store
          </a>
        </div>
        <WebsiteLink />
      </VendorOfferShell>
    );
  }

  if (phase === 'thanks') {
    const thanks = definition?.thank_you;
    return (
      <VendorOfferShell branding={branding} showBranding={false} layout="questionnaire">
        <div className="questionnaire-body">
          <div className="questionnaire-thanks">
            <p className="questionnaire-kicker">{thanks?.eyebrow || 'Questionnaire complete'}</p>
            <div className="questionnaire-copy-block">
              <h1 className="questionnaire-prompt">{thanks?.title || 'Thank you,'}</h1>
              {(thanks?.paragraphs || [
                'Your answers are in. You can start your baseline on 16 November.',
                'Keep wearing your device day and night from 16 November, and do not open HEAL until 30 November.',
              ]).map((paragraph) => (
                <p className="questionnaire-copy" key={paragraph}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
        <div className="questionnaire-footer">
          <button type="button" className="questionnaire-next ready" onClick={() => setPhase('linked')}>
            Done
          </button>
        </div>
      </VendorOfferShell>
    );
  }

  if (phase === 'declined') {
    return (
      <VendorOfferShell branding={branding} showBranding={false} layout="questionnaire">
        <div className="questionnaire-body">
          <h1 className="questionnaire-title">Consent needed</h1>
          <p className="questionnaire-copy">
            You need to consent before taking part in this pilot. Your answers are saved. You can change your answer if you decide to continue.
          </p>
        </div>
        <div className="questionnaire-footer">
          <button type="button" className="questionnaire-next ready" onClick={() => setPhase('questionnaire')}>
            Back to consent
          </button>
          <WebsiteLink />
        </div>
      </VendorOfferShell>
    );
  }

  if (!definition || !session || !claimId || !resumeToken) {
    return (
      <VendorOfferShell branding={branding}>
        <div className="vendor-offer-main">
          <p className="vendor-offer-subtitle vendor-offer-loading">Loading your questionnaire…</p>
        </div>
      </VendorOfferShell>
    );
  }

  return (
    <QuestionnaireStep
      key={claimId}
      claimId={claimId}
      resumeToken={resumeToken}
      definition={definition}
      initialSession={session}
      onSession={setSession}
      onDeclined={() => setPhase('declined')}
      onThanks={() => setPhase('thanks')}
    />
  );
};

export default NhsTrialFlow;
